import {
  claimCarrion,
  isFreshCarrion,
  isLivingKind,
  isScavengerCarrion,
  turnCreatureIntoCorpse,
} from './carrion';
import {
  CELESTIAL_BODY_SPECS,
  STAR_COLLISION_RADIUS,
  STAR_INDEX,
} from './celestial-system';
import { EntityKind, type PrimaryStore } from './primary-store';
import type { SpatialHash } from './spatial-hash';
import { BehaviorMode } from './behavior';
import { EmissionKind, type EmissionQueue } from './emissions';
import type { PlanetVegetationField } from './vegetation-field';

const MAXIMUM_LIVING_RADIUS = 0.005;
const COLLISION_RADIUS_BY_KIND = new Float64Array([
  0,
  0.00055,
  0.0003,
  0.005,
  0,
  0,
  0,
  0.0007,
  0.0007,
  0.002,
  0.00032,
]);
const PAIR_COLLIDER_KIND_MASK =
  (1 << EntityKind.Herbivore) |
  (1 << EntityKind.Predator) |
  (1 << EntityKind.Scavenger) |
  (1 << EntityKind.Corpse) |
  (1 << EntityKind.Comet) |
  (1 << EntityKind.Asteroid);
const LIVING_RESTITUTION = 0.68;
const CORPSE_RESTITUTION = 0.12;
const LIFE_BODY_RESTITUTION = 0.9;
const CELESTIAL_BODY_RESTITUTION = 0.08;
const SEPARATION_EPSILON = 1e-7;
/** Exact predator-herbivore contact distance used by the pair solver. */
export const PREDATOR_CONTACT_RADIUS = 0.0053;
const SWEPT_GRID_POPULATION_THRESHOLD = 1_024;
const terminalTimesByStore = new WeakMap<PrimaryStore, Float64Array>();

// Broad-phase cells need only be fine enough to keep candidate sets local;
// exact swept geometry below still decides every collision. A larger solar
// domain must not spend each tick clearing millions of empty cells.
export const COLLISION_HASH_CELL_SIZE = 0.02;

export function collisionRadiusForKind(kind: EntityKind): number {
  return COLLISION_RADIUS_BY_KIND[kind] ?? 0;
}

export function snapshotCollisionPositions(
  store: PrimaryStore,
  previousX: Float64Array,
  previousY: Float64Array,
): void {
  previousX.set(store.x.subarray(0, store.count), 0);
  previousY.set(store.y.subarray(0, store.count), 0);
}

export function resolveEntityCollisions(
  store: PrimaryStore,
  collisionHash: SpatialHash,
  candidateScratch: Int32Array,
  previousX: Float64Array,
  previousY: Float64Array,
  deltaSeconds: number,
  emissions?: EmissionQueue,
  vegetation?: PlanetVegetationField,
  dirtyIndices?: Int32Array,
  dirtyMarks?: Uint8Array,
): void {
  const scaledStep = Math.max(0, deltaSeconds);
  let dirtyCount = 0;
  dirtyMarks?.fill(0, 0, store.count);
  let terminalTimes = terminalTimesByStore.get(store);
  if (terminalTimes === undefined) {
    terminalTimes = new Float64Array(store.capacity);
    terminalTimesByStore.set(store, terminalTimes);
  }
  terminalTimes.fill(1, 0, store.count);

  resolveCelestialBodyCollisions(
    store,
    previousX,
    previousY,
    scaledStep,
  );

  for (let index = 0; index < store.count; index += 1) {
    if (isProjectile(store.kind[index] ?? EntityKind.Inactive)) {
      terminalTimes[index] = firstBodyContact(store, index, previousX, previousY)?.time ?? 1;
      continue;
    }
    if (isSolidBodyCollider(store.kind[index] ?? EntityKind.Inactive)) {
      resolveBodyCollisions(
        store,
        index,
        previousX,
        previousY,
        scaledStep,
        emissions,
      );
    }
  }

  const useSweptGrid = store.count >= SWEPT_GRID_POPULATION_THRESHOLD;
  if (useSweptGrid) {
    collisionHash.rebuildSwept(
      store,
      previousX,
      previousY,
      COLLISION_RADIUS_BY_KIND,
      scaledStep,
    );
  } else {
    collisionHash.rebuild(store);
  }
  let maximumTravel = 0;
  if (!useSweptGrid) {
    for (let index = 0; index < store.count; index += 1) {
      if (!isPairCollider(store.kind[index] ?? EntityKind.Inactive)) {
        continue;
      }
      maximumTravel = Math.max(
        maximumTravel,
        Math.hypot(
          (store.x[index] ?? 0) - (previousX[index] ?? 0),
          (store.y[index] ?? 0) - (previousY[index] ?? 0),
        ),
      );
    }
  }
  if (!useSweptGrid) {
    for (let first = 0; first < store.count; first += 1) {
      if (!isPairCollider(store.kind[first] ?? EntityKind.Inactive)) {
        continue;
      }
      const firstTravel = Math.hypot(
          (store.x[first] ?? 0) - (previousX[first] ?? 0),
          (store.y[first] ?? 0) - (previousY[first] ?? 0),
        );
      const candidates = collisionHash.queryInto(
        store.x[first] ?? 0,
        store.y[first] ?? 0,
        collisionRadiusForKind(store.kind[first] ?? EntityKind.Inactive) +
          MAXIMUM_LIVING_RADIUS +
          firstTravel +
          maximumTravel,
        candidateScratch,
      );
      for (let cursor = 0; cursor < candidates; cursor += 1) {
        const second = candidateScratch[cursor] ?? -1;
        if (
          second <= first ||
          !isPairCollider(store.kind[second] ?? EntityKind.Inactive)
        ) {
          continue;
        }
        if (
          resolveSweptPair(
            store,
            first,
            second,
            previousX,
            previousY,
            scaledStep,
            emissions,
            vegetation,
            undefined,
            undefined,
            terminalTimes,
          )
        ) {
          dirtyCount = markDirty(
            first,
            dirtyIndices,
            dirtyMarks,
            dirtyCount,
          );
          dirtyCount = markDirty(
            second,
            dirtyIndices,
            dirtyMarks,
            dirtyCount,
          );
        }
      }
    }
  } else {
    collisionHash.forEachSweptPair(
      PAIR_COLLIDER_KIND_MASK,
      (first, second, timeSlice) => {
        if (
          resolveSweptPair(
            store,
            first,
            second,
            previousX,
            previousY,
            scaledStep,
            emissions,
            vegetation,
            timeSlice,
            collisionHash.sweptTimeSliceCount,
            terminalTimes,
          )
        ) {
          dirtyCount = markDirty(
            first,
            dirtyIndices,
            dirtyMarks,
            dirtyCount,
          );
          dirtyCount = markDirty(
            second,
            dirtyIndices,
            dirtyMarks,
            dirtyCount,
          );
        }
      },
    );
  }
  // Finish terminal impacts only after contacts on the incoming segment.
  for (let index = 0; index < store.count; index += 1) {
    if (isProjectile(store.kind[index] ?? EntityKind.Inactive)) {
      resolveBodyCollisions(store, index, previousX, previousY, scaledStep, emissions);
    }
  }
  resolvePairOverlaps(
    store,
    collisionHash,
    candidateScratch,
    emissions,
    vegetation,
    dirtyIndices,
    dirtyMarks,
    dirtyCount,
  );

  // Pair separation can push an organism into a solid world.
  for (let index = 0; index < store.count; index += 1) {
    if (isSolidBodyCollider(store.kind[index] ?? EntityKind.Inactive)) {
      projectOutsideBodies(store, index, emissions);
    }
  }
}

/**
 * Resolves contact between every registered spherical body. The body count is
 * deliberately small, so the exact all-pairs pass is cheaper and more robust
 * than routing these massive objects through the organism broad phase.
 */
export function resolveCelestialBodyCollisions(
  store: PrimaryStore,
  previousX: Float64Array,
  previousY: Float64Array,
  deltaSeconds: number,
): void {
  const safeDelta = Math.max(0, deltaSeconds);
  if ((store.kind[STAR_INDEX] ?? EntityKind.Inactive) === EntityKind.Star) {
    for (const body of CELESTIAL_BODY_SPECS) {
      if (!isSolidBodyKind(store.kind[body.index] ?? EntityKind.Inactive)) {
        continue;
      }
      resolveSweptCelestialBodyPair(
        store,
        STAR_INDEX,
        STAR_COLLISION_RADIUS,
        body.index,
        body.bodyRadius,
        previousX,
        previousY,
        safeDelta,
      );
    }
  }
  for (let firstSlot = 0; firstSlot < CELESTIAL_BODY_SPECS.length; firstSlot += 1) {
    const firstBody = CELESTIAL_BODY_SPECS[firstSlot];
    if (
      firstBody === undefined ||
      !isSolidBodyKind(store.kind[firstBody.index] ?? EntityKind.Inactive)
    ) {
      continue;
    }
    for (
      let secondSlot = firstSlot + 1;
      secondSlot < CELESTIAL_BODY_SPECS.length;
      secondSlot += 1
    ) {
      const secondBody = CELESTIAL_BODY_SPECS[secondSlot];
      if (
        secondBody === undefined ||
        !isSolidBodyKind(store.kind[secondBody.index] ?? EntityKind.Inactive)
      ) {
        continue;
      }
      resolveSweptCelestialBodyPair(
        store,
        firstBody.index,
        firstBody.bodyRadius,
        secondBody.index,
        secondBody.bodyRadius,
        previousX,
        previousY,
        safeDelta,
      );
    }
  }
}

function resolveSweptCelestialBodyPair(
  store: PrimaryStore,
  first: number,
  firstRadius: number,
  second: number,
  secondRadius: number,
  previousX: Float64Array,
  previousY: Float64Array,
  deltaSeconds: number,
): void {
  const combinedRadius = firstRadius + secondRadius;
  const firstStartX = previousX[first] ?? store.x[first] ?? 0;
  const firstStartY = previousY[first] ?? store.y[first] ?? 0;
  const secondStartX = previousX[second] ?? store.x[second] ?? 0;
  const secondStartY = previousY[second] ?? store.y[second] ?? 0;
  const firstEndX = store.x[first] ?? firstStartX;
  const firstEndY = store.y[first] ?? firstStartY;
  const secondEndX = store.x[second] ?? secondStartX;
  const secondEndY = store.y[second] ?? secondStartY;
  const relativeStartX = firstStartX - secondStartX;
  const relativeStartY = firstStartY - secondStartY;
  const relativeTravelX =
    (firstEndX - firstStartX) - (secondEndX - secondStartX);
  const relativeTravelY =
    (firstEndY - firstStartY) - (secondEndY - secondStartY);
  const hitTime = firstCircleHit(
    relativeStartX,
    relativeStartY,
    relativeTravelX,
    relativeTravelY,
    combinedRadius,
  );
  const finalRelativeX = firstEndX - secondEndX;
  const finalRelativeY = firstEndY - secondEndY;
  if (
    hitTime === undefined &&
    finalRelativeX * finalRelativeX + finalRelativeY * finalRelativeY >=
      combinedRadius * combinedRadius
  ) {
    return;
  }

  const time = hitTime ?? 1;
  const firstTravelX = firstEndX - firstStartX;
  const firstTravelY = firstEndY - firstStartY;
  const secondTravelX = secondEndX - secondStartX;
  const secondTravelY = secondEndY - secondStartY;
  let normalX =
    firstStartX + firstTravelX * time -
    (secondStartX + secondTravelX * time);
  let normalY =
    firstStartY + firstTravelY * time -
    (secondStartY + secondTravelY * time);
  [normalX, normalY] = normalizedContactNormal(
    normalX,
    normalY,
    first,
    second,
  );

  const inverseFirstMass = 1 / Math.max(1e-9, store.mass[first] ?? 1);
  const inverseSecondMass = 1 / Math.max(1e-9, store.mass[second] ?? 1);
  const inverseMassSum = inverseFirstMass + inverseSecondMass;
  const relativeVx = (store.vx[first] ?? 0) - (store.vx[second] ?? 0);
  const relativeVy = (store.vy[first] ?? 0) - (store.vy[second] ?? 0);
  const inwardSpeed = relativeVx * normalX + relativeVy * normalY;
  if (inwardSpeed < 0) {
    const impulse =
      (-(1 + CELESTIAL_BODY_RESTITUTION) * inwardSpeed) / inverseMassSum;
    store.vx[first] =
      (store.vx[first] ?? 0) + impulse * inverseFirstMass * normalX;
    store.vy[first] =
      (store.vy[first] ?? 0) + impulse * inverseFirstMass * normalY;
    store.vx[second] =
      (store.vx[second] ?? 0) - impulse * inverseSecondMass * normalX;
    store.vy[second] =
      (store.vy[second] ?? 0) - impulse * inverseSecondMass * normalY;
  }

  const firstContactX = firstStartX + firstTravelX * time;
  const firstContactY = firstStartY + firstTravelY * time;
  const secondContactX = secondStartX + secondTravelX * time;
  const secondContactY = secondStartY + secondTravelY * time;
  const remainingSeconds = deltaSeconds * Math.max(0, 1 - time);
  store.x[first] = firstContactX + (store.vx[first] ?? 0) * remainingSeconds;
  store.y[first] = firstContactY + (store.vy[first] ?? 0) * remainingSeconds;
  store.x[second] = secondContactX + (store.vx[second] ?? 0) * remainingSeconds;
  store.y[second] = secondContactY + (store.vy[second] ?? 0) * remainingSeconds;

  const separationX = (store.x[first] ?? 0) - (store.x[second] ?? 0);
  const separationY = (store.y[first] ?? 0) - (store.y[second] ?? 0);
  const separation = Math.hypot(separationX, separationY);
  const penetration = combinedRadius + SEPARATION_EPSILON - separation;
  if (penetration <= 0) {
    return;
  }
  const correctionX = normalX * penetration;
  const correctionY = normalY * penetration;
  const firstShare = inverseFirstMass / inverseMassSum;
  const secondShare = inverseSecondMass / inverseMassSum;
  store.x[first] = (store.x[first] ?? 0) + correctionX * firstShare;
  store.y[first] = (store.y[first] ?? 0) + correctionY * firstShare;
  store.x[second] = (store.x[second] ?? 0) - correctionX * secondShare;
  store.y[second] = (store.y[second] ?? 0) - correctionY * secondShare;
}

export function resolveCurrentEntityOverlaps(
  store: PrimaryStore,
  collisionHash: SpatialHash,
  candidateScratch: Int32Array,
  emissions?: EmissionQueue,
  vegetation?: PlanetVegetationField,
): void {
  resolvePairOverlaps(
    store,
    collisionHash,
    candidateScratch,
    emissions,
    vegetation,
  );
  for (let index = 0; index < store.count; index += 1) {
    if (isSolidBodyCollider(store.kind[index] ?? EntityKind.Inactive)) {
      projectOutsideBodies(store, index, emissions);
    }
  }
}

function resolvePairOverlaps(
  store: PrimaryStore,
  collisionHash: SpatialHash,
  candidateScratch: Int32Array,
  emissions?: EmissionQueue,
  vegetation?: PlanetVegetationField,
  dirtyIndices?: Int32Array,
  dirtyMarks?: Uint8Array,
  initialDirtyCount = 0,
): void {
  // One bounded Gauss-Seidel pass settles ordinary chained contacts without
  // turning dense ecology bands into an unbounded packing solve.
  for (let iteration = 0; iteration < 1; iteration += 1) {
    collisionHash.rebuild(store);
    let dirtyCount = initialDirtyCount;
    for (
      let work = 0;
      work < (dirtyIndices === undefined ? store.count : dirtyCount);
      work += 1
    ) {
      const first = dirtyIndices?.[work] ?? work;
      if (!isPairCollider(store.kind[first] ?? EntityKind.Inactive)) {
        continue;
      }
      const candidates = collisionHash.queryInto(
        store.x[first] ?? 0,
        store.y[first] ?? 0,
        collisionRadiusForKind(store.kind[first] ?? EntityKind.Inactive) +
          MAXIMUM_LIVING_RADIUS +
          SEPARATION_EPSILON,
        candidateScratch,
      );
      for (let cursor = 0; cursor < candidates; cursor += 1) {
        const second = candidateScratch[cursor] ?? -1;
        if (
          second === first ||
          (dirtyIndices === undefined && second < first) ||
          !isPairCollider(store.kind[second] ?? EntityKind.Inactive)
        ) {
          continue;
        }
        const combinedRadius =
          collisionRadiusForKind(store.kind[first] ?? EntityKind.Inactive) +
          collisionRadiusForKind(store.kind[second] ?? EntityKind.Inactive);
        if (
          Math.abs((store.x[first] ?? 0) - (store.x[second] ?? 0)) >=
            combinedRadius ||
          Math.abs((store.y[first] ?? 0) - (store.y[second] ?? 0)) >=
            combinedRadius
        ) {
          continue;
        }
        if (separatePair(store, first, second, emissions, vegetation)) {
          dirtyCount = markDirty(
            second,
            dirtyIndices,
            dirtyMarks,
            dirtyCount,
          );
        }
      }
    }
  }
}

function resolveBodyCollisions(
  store: PrimaryStore,
  life: number,
  previousX: Float64Array,
  previousY: Float64Array,
  scaledStep: number,
  emissions?: EmissionQueue,
): void {
  const contact = firstBodyContact(store, life, previousX, previousY);
  if (contact !== undefined) resolveSweptBody(store, life, contact.body, contact.radius,
    previousX, previousY, scaledStep, true, contact.body === STAR_INDEX ? undefined : emissions);
}

function firstBodyContact(store: PrimaryStore, life: number, previousX: Float64Array,
  previousY: Float64Array): { body: number; radius: number; time: number } | undefined {
  let contact: { body: number; radius: number; time: number } | undefined;
  const consider = (index: number, radius: number) => {
    const x = (previousX[life] ?? 0) - (previousX[index] ?? 0);
    const y = (previousY[life] ?? 0) - (previousY[index] ?? 0);
    const time = firstCircleHit(x, y,
      (store.x[life] ?? 0) - (store.x[index] ?? 0) - x,
      (store.y[life] ?? 0) - (store.y[index] ?? 0) - y,
      radius + collisionRadiusForKind(store.kind[life] ?? EntityKind.Inactive));
    if (time !== undefined && (contact === undefined || time < contact.time)) contact = { body: index, radius, time };
  };
  if (store.kind[STAR_INDEX] === EntityKind.Star) consider(STAR_INDEX, STAR_COLLISION_RADIUS);
  for (const body of CELESTIAL_BODY_SPECS) {
    if (!isSolidBodyKind(store.kind[body.index] ?? EntityKind.Inactive)) {
      continue;
    }
    consider(body.index, body.bodyRadius);
  }
  return contact;
}

function isProjectile(kind: EntityKind): boolean { return kind === EntityKind.Comet || kind === EntityKind.Asteroid; }

function resolveSweptBody(
  store: PrimaryStore,
  life: number,
  body: number,
  bodyRadius: number,
  previousX: Float64Array,
  previousY: Float64Array,
  scaledStep: number,
  destroyOnContact: boolean,
  impactEmissions?: EmissionQueue,
): void {
  const combinedRadius =
    bodyRadius +
    collisionRadiusForKind(store.kind[life] ?? EntityKind.Inactive);
  const relativeStartX =
    (previousX[life] ?? store.x[life] ?? 0) -
    (previousX[body] ?? store.x[body] ?? 0);
  const relativeStartY =
    (previousY[life] ?? store.y[life] ?? 0) -
    (previousY[body] ?? store.y[body] ?? 0);
  const relativeEndX = (store.x[life] ?? 0) - (store.x[body] ?? 0);
  const relativeEndY = (store.y[life] ?? 0) - (store.y[body] ?? 0);
  const travelX = relativeEndX - relativeStartX;
  const travelY = relativeEndY - relativeStartY;
  const hitTime = firstCircleHit(
    relativeStartX,
    relativeStartY,
    travelX,
    travelY,
    combinedRadius,
  );
  const finalDistanceSquared =
    relativeEndX * relativeEndX + relativeEndY * relativeEndY;
  if (hitTime === undefined && finalDistanceSquared >= combinedRadius ** 2) {
    return;
  }

  if (destroyOnContact) {
    const projectileKind = store.kind[life] ?? EntityKind.Inactive;
    if (
      (projectileKind === EntityKind.Comet ||
        projectileKind === EntityKind.Asteroid) &&
      impactEmissions !== undefined
    ) {
      let normalX = relativeStartX + travelX * (hitTime ?? 1);
      let normalY = relativeStartY + travelY * (hitTime ?? 1);
      [normalX, normalY] = normalizedContactNormal(
        normalX,
        normalY,
        store.lineage[life] ?? life,
        body,
      );
      const time = hitTime ?? 1;
      const bodyX =
        (previousX[body] ?? store.x[body] ?? 0) +
        ((store.x[body] ?? 0) - (previousX[body] ?? 0)) * time;
      const bodyY =
        (previousY[body] ?? store.y[body] ?? 0) +
        ((store.y[body] ?? 0) - (previousY[body] ?? 0)) * time;
      impactEmissions.push(
        projectileKind === EntityKind.Asteroid
          ? EmissionKind.AsteroidImpact
          : EmissionKind.CometImpact,
        bodyX + normalX * bodyRadius,
        bodyY + normalY * bodyRadius,
        Math.max(0.8, store.mass[life] ?? 0.8),
        body, normalX * bodyRadius, normalY * bodyRadius,
      );
    }
    store.matter?.discard(life, body);
    store.kind[life] = EntityKind.Inactive;
    return;
  }

  const time = hitTime ?? 1;
  let normalX = relativeStartX + travelX * time;
  let normalY = relativeStartY + travelY * time;
  [normalX, normalY] = normalizedContactNormal(
    normalX,
    normalY,
    store.lineage[life] ?? life,
    body,
  );

  const bodyVx = store.vx[body] ?? 0;
  const bodyVy = store.vy[body] ?? 0;
  let relativeVx = (store.vx[life] ?? 0) - bodyVx;
  let relativeVy = (store.vy[life] ?? 0) - bodyVy;
  const inwardSpeed = relativeVx * normalX + relativeVy * normalY;
  if (inwardSpeed < 0) {
    relativeVx -= (1 + LIFE_BODY_RESTITUTION) * inwardSpeed * normalX;
    relativeVy -= (1 + LIFE_BODY_RESTITUTION) * inwardSpeed * normalY;
  }
  store.vx[life] = bodyVx + relativeVx;
  store.vy[life] = bodyVy + relativeVy;

  const remainingTime = scaledStep * Math.max(0, 1 - time);
  store.x[life] =
    (store.x[body] ?? 0) +
    normalX * (combinedRadius + SEPARATION_EPSILON) +
    relativeVx * remainingTime;
  store.y[life] =
    (store.y[body] ?? 0) +
    normalY * (combinedRadius + SEPARATION_EPSILON) +
    relativeVy * remainingTime;
  projectOutsideBody(store, life, body, combinedRadius);
}

function resolveSweptPair(
  store: PrimaryStore,
  first: number,
  second: number,
  previousX: Float64Array,
  previousY: Float64Array,
  scaledStep: number,
  emissions?: EmissionQueue,
  vegetation?: PlanetVegetationField,
  timeSlice?: number,
  timeSliceCount?: number,
  terminalTimes?: Float64Array,
): boolean {
  const combinedRadius =
    collisionRadiusForKind(store.kind[first] ?? EntityKind.Inactive) +
    collisionRadiusForKind(store.kind[second] ?? EntityKind.Inactive);
  const startX = (previousX[first] ?? 0) - (previousX[second] ?? 0);
  const startY = (previousY[first] ?? 0) - (previousY[second] ?? 0);
  const endX = (store.x[first] ?? 0) - (store.x[second] ?? 0);
  const endY = (store.y[first] ?? 0) - (store.y[second] ?? 0);
  const hitTime = firstCircleHit(
    startX,
    startY,
    endX - startX,
    endY - startY,
    combinedRadius,
  );
  if (hitTime === undefined || hitTime > Math.min(terminalTimes?.[first] ?? 1, terminalTimes?.[second] ?? 1)) {
    return false;
  }
  if (
    timeSlice !== undefined &&
    timeSliceCount !== undefined &&
    Math.min(timeSliceCount - 1, Math.floor(hitTime * timeSliceCount)) !==
      timeSlice
  ) {
    return false;
  }

  const firstKindAtContact = store.kind[first] ?? EntityKind.Inactive;
  const secondKindAtContact = store.kind[second] ?? EntityKind.Inactive;
  const cometContact =
    firstKindAtContact === EntityKind.Comet ||
    secondKindAtContact === EntityKind.Comet;
  const cometCounterpartKind =
    firstKindAtContact === EntityKind.Comet
      ? secondKindAtContact
      : firstKindAtContact;
  const asteroidContact =
    firstKindAtContact === EntityKind.Asteroid ||
    secondKindAtContact === EntityKind.Asteroid;
  const asteroidCounterpartKind =
    firstKindAtContact === EntityKind.Asteroid
      ? secondKindAtContact
      : firstKindAtContact;
  handleEcologyContact(store, first, second, emissions, vegetation);
  // A comet punches through biology and carrion without a second overlap
  // callback deleting the newly made corpse. Asteroids still exchange a
  // physical impulse with it; only celestial-body contacts consume it.
  if (cometContact && cometCounterpartKind !== EntityKind.Asteroid) {
    return true;
  }
  // Belt asteroids remain ballistic after punching through life. Turning the
  // victim into carrion must not let its much greater biological mass bounce
  // the tiny gravity-effector asteroid away from the rest of its trajectory.
  if (asteroidContact && isLivingKind(asteroidCounterpartKind)) {
    return true;
  }
  if (
    store.kind[first] === EntityKind.Inactive ||
    store.kind[second] === EntityKind.Inactive
  ) {
    return true;
  }

  let normalX = startX + (endX - startX) * hitTime;
  let normalY = startY + (endY - startY) * hitTime;
  [normalX, normalY] = normalizedContactNormal(
    normalX,
    normalY,
    store.lineage[first] ?? first,
    store.lineage[second] ?? second,
  );
  applyPairImpulse(store, first, second, normalX, normalY);

  const firstImpactX =
    (previousX[first] ?? 0) +
    ((store.x[first] ?? 0) - (previousX[first] ?? 0)) * hitTime;
  const firstImpactY =
    (previousY[first] ?? 0) +
    ((store.y[first] ?? 0) - (previousY[first] ?? 0)) * hitTime;
  const secondImpactX =
    (previousX[second] ?? 0) +
    ((store.x[second] ?? 0) - (previousX[second] ?? 0)) * hitTime;
  const secondImpactY =
    (previousY[second] ?? 0) +
    ((store.y[second] ?? 0) - (previousY[second] ?? 0)) * hitTime;
  const remainingTime = scaledStep * Math.max(0, 1 - hitTime);
  store.x[first] = firstImpactX + (store.vx[first] ?? 0) * remainingTime;
  store.y[first] = firstImpactY + (store.vy[first] ?? 0) * remainingTime;
  store.x[second] = secondImpactX + (store.vx[second] ?? 0) * remainingTime;
  store.y[second] = secondImpactY + (store.vy[second] ?? 0) * remainingTime;
  separatePair(store, first, second, emissions, vegetation);
  return true;
}

function separatePair(
  store: PrimaryStore,
  first: number,
  second: number,
  emissions?: EmissionQueue,
  vegetation?: PlanetVegetationField,
): boolean {
  const combinedRadius =
    collisionRadiusForKind(store.kind[first] ?? EntityKind.Inactive) +
    collisionRadiusForKind(store.kind[second] ?? EntityKind.Inactive);
  let dx = (store.x[first] ?? 0) - (store.x[second] ?? 0);
  let dy = (store.y[first] ?? 0) - (store.y[second] ?? 0);
  const distance = Math.hypot(dx, dy);
  if (distance >= combinedRadius) {
    return false;
  }
  const firstKindAtContact = store.kind[first] ?? EntityKind.Inactive;
  const secondKindAtContact = store.kind[second] ?? EntityKind.Inactive;
  const asteroidContact =
    firstKindAtContact === EntityKind.Asteroid ||
    secondKindAtContact === EntityKind.Asteroid;
  const asteroidCounterpartKind =
    firstKindAtContact === EntityKind.Asteroid
      ? secondKindAtContact
      : firstKindAtContact;
  handleEcologyContact(store, first, second, emissions, vegetation);
  if (asteroidContact && isLivingKind(asteroidCounterpartKind)) {
    return true;
  }
  if (
    store.kind[first] === EntityKind.Inactive ||
    store.kind[second] === EntityKind.Inactive
  ) {
    return true;
  }
  [dx, dy] = normalizedContactNormal(
    dx,
    dy,
    store.lineage[first] ?? first,
    store.lineage[second] ?? second,
  );
  const inverseFirstMass = 1 / Math.max(0.02, store.mass[first] ?? 0.02);
  const inverseSecondMass = 1 / Math.max(0.02, store.mass[second] ?? 0.02);
  const inverseMassSum = inverseFirstMass + inverseSecondMass;
  const penetration =
    (combinedRadius - distance) * 1.5 + SEPARATION_EPSILON;
  store.x[first] =
    (store.x[first] ?? 0) +
    dx * penetration * (inverseFirstMass / inverseMassSum);
  store.y[first] =
    (store.y[first] ?? 0) +
    dy * penetration * (inverseFirstMass / inverseMassSum);
  store.x[second] =
    (store.x[second] ?? 0) -
    dx * penetration * (inverseSecondMass / inverseMassSum);
  store.y[second] =
    (store.y[second] ?? 0) -
    dy * penetration * (inverseSecondMass / inverseMassSum);
  applyPairImpulse(store, first, second, dx, dy);
  return true;
}

function markDirty(
  index: number,
  dirtyIndices: Int32Array | undefined,
  dirtyMarks: Uint8Array | undefined,
  dirtyCount: number,
): number {
  if (
    dirtyIndices === undefined ||
    dirtyMarks === undefined ||
    index < 0 ||
    dirtyMarks[index] !== 0 ||
    dirtyCount >= dirtyIndices.length
  ) {
    return dirtyCount;
  }
  dirtyMarks[index] = 1;
  dirtyIndices[dirtyCount] = index;
  return dirtyCount + 1;
}

export function handleEcologyContact(
  store: PrimaryStore,
  first: number,
  second: number,
  emissions?: EmissionQueue,
  vegetation?: PlanetVegetationField,
): void {
  const firstKind = store.kind[first] ?? EntityKind.Inactive;
  const secondKind = store.kind[second] ?? EntityKind.Inactive;
  if (firstKind === EntityKind.Comet || secondKind === EntityKind.Comet) {
    const comet = firstKind === EntityKind.Comet ? first : second;
    const struck = comet === first ? second : first;
    const struckKind = store.kind[struck] ?? EntityKind.Inactive;
    if (isLivingKind(struckKind)) {
      vegetation?.recordCarrionSignal(
        store,
        store.x[struck] ?? 0,
        store.y[struck] ?? 0,
        store.mass[struck] ?? 0.25,
      );
      turnCreatureIntoCorpse(
        store,
        struck,
        emissions,
        EmissionKind.Fragmentation,
      );
    } else if (struckKind === EntityKind.Corpse) {
      store.matter?.discard(struck, 'space');
      store.kind[struck] = EntityKind.Inactive;
    }
    return;
  }
  if (firstKind === EntityKind.Asteroid || secondKind === EntityKind.Asteroid) {
    const asteroid = firstKind === EntityKind.Asteroid ? first : second;
    const struck = asteroid === first ? second : first;
    const struckKind = store.kind[struck] ?? EntityKind.Inactive;
    if (isLivingKind(struckKind)) {
      vegetation?.recordCarrionSignal(
        store,
        store.x[struck] ?? 0,
        store.y[struck] ?? 0,
        store.mass[struck] ?? 0.25,
      );
      turnCreatureIntoCorpse(
        store,
        struck,
        emissions,
        EmissionKind.Fragmentation,
      );
    }
    return;
  }
  if (
    firstKind === EntityKind.Predator &&
    secondKind === EntityKind.Predator
  ) {
    const predator = predatorCombatWinner(store, first, second);
    const prey = predator === first ? second : first;
    turnCreatureIntoCorpse(store, prey, emissions, EmissionKind.Kill);
    store.foragingSuccess[predator] = Math.min(
      1,
      (store.foragingSuccess[predator] ?? 0) + 0.28,
    );
    vegetation?.recordCarrionSignal(
      store,
      store.x[prey] ?? 0,
      store.y[prey] ?? 0,
      store.mass[prey] ?? 0.5,
    );
  }
  const predator =
    firstKind === EntityKind.Predator &&
    isPredatorPreyContact(store, first, second, secondKind)
      ? first
      : secondKind === EntityKind.Predator &&
          isPredatorPreyContact(store, second, first, firstKind)
        ? second
        : -1;
  if (predator >= 0) {
    const prey = predator === first ? second : first;
    turnCreatureIntoCorpse(store, prey, emissions, EmissionKind.Kill);
    store.foragingSuccess[predator] = Math.min(
      1,
      (store.foragingSuccess[predator] ?? 0) + 0.28,
    );
    vegetation?.recordCarrionSignal(
      store,
      store.x[prey] ?? 0,
      store.y[prey] ?? 0,
      store.mass[prey] ?? 0.5,
    );
  }

  const updatedFirstKind = store.kind[first] ?? EntityKind.Inactive;
  const updatedSecondKind = store.kind[second] ?? EntityKind.Inactive;
  const consumer =
    isCarrionConsumer(updatedFirstKind) && updatedSecondKind === EntityKind.Corpse
      ? first
      : isCarrionConsumer(updatedSecondKind) && updatedFirstKind === EntityKind.Corpse
        ? second
        : -1;
  if (consumer < 0) {
    return;
  }
  const corpse = consumer === first ? second : first;
  const eligibleCarrion =
    store.kind[consumer] === EntityKind.Predator
      ? isFreshCarrion(store, corpse)
      : isScavengerCarrion(store, corpse);
  if (!eligibleCarrion || !claimCarrion(store, consumer, corpse)) {
    return;
  }
  // Contact must not replace a registry-slot migration destination with an
  // entity index. Migrants feed within reach in the ordinary ecology step.
  if (store.behavior[consumer] === BehaviorMode.Migrate) return;
  store.behavior[consumer] = BehaviorMode.Feed;
  store.behaviorTarget[consumer] = corpse;
  store.behaviorTimer[consumer] = 3.5;
  store.handlingTimer[consumer] = 0;
  store.memoryX[consumer] = store.x[corpse] ?? store.x[consumer] ?? 0;
  store.memoryY[consumer] = store.y[corpse] ?? store.y[consumer] ?? 0;
  store.memoryTimer[consumer] = 8;
}

function predatorCombatWinner(
  store: PrimaryStore,
  first: number,
  second: number,
): number {
  const firstFitness =
    Math.max(0, store.mass[first] ?? 0) +
    Math.sqrt(Math.max(0, store.energy[first] ?? 0)) * 0.25;
  const secondFitness =
    Math.max(0, store.mass[second] ?? 0) +
    Math.sqrt(Math.max(0, store.energy[second] ?? 0)) * 0.25;
  if (Math.abs(firstFitness - secondFitness) > 1e-9) {
    return firstFitness > secondFitness ? first : second;
  }
  const firstLineage = store.lineage[first] ?? first;
  const secondLineage = store.lineage[second] ?? second;
  return firstLineage <= secondLineage ? first : second;
}

function isPredatorPreyContact(
  store: PrimaryStore,
  predator: number,
  prey: number,
  preyKind: EntityKind,
): boolean {
  return (
    store.behavior[predator] === BehaviorMode.Hunt &&
    (preyKind === EntityKind.Herbivore ||
      (preyKind === EntityKind.Scavenger &&
        (store.behaviorTarget[predator] ?? -1) === prey))
  );
}

function isCarrionConsumer(kind: EntityKind): boolean {
  return kind === EntityKind.Predator || kind === EntityKind.Scavenger;
}

function applyPairImpulse(
  store: PrimaryStore,
  first: number,
  second: number,
  normalX: number,
  normalY: number,
): void {
  const relativeVx = (store.vx[first] ?? 0) - (store.vx[second] ?? 0);
  const relativeVy = (store.vy[first] ?? 0) - (store.vy[second] ?? 0);
  const inwardSpeed = relativeVx * normalX + relativeVy * normalY;
  if (inwardSpeed >= 0) {
    return;
  }
  const inverseFirstMass = 1 / Math.max(0.02, store.mass[first] ?? 0.02);
  const inverseSecondMass = 1 / Math.max(0.02, store.mass[second] ?? 0.02);
  const restitution =
    store.kind[first] === EntityKind.Corpse ||
    store.kind[second] === EntityKind.Corpse
      ? CORPSE_RESTITUTION
      : LIVING_RESTITUTION;
  const impulse =
    (-(1 + restitution) * inwardSpeed) /
    (inverseFirstMass + inverseSecondMass);
  store.vx[first] =
    (store.vx[first] ?? 0) + impulse * inverseFirstMass * normalX;
  store.vy[first] =
    (store.vy[first] ?? 0) + impulse * inverseFirstMass * normalY;
  store.vx[second] =
    (store.vx[second] ?? 0) - impulse * inverseSecondMass * normalX;
  store.vy[second] =
    (store.vy[second] ?? 0) - impulse * inverseSecondMass * normalY;
}

function projectOutsideBodies(
  store: PrimaryStore,
  life: number,
  impactEmissions?: EmissionQueue,
): void {
  const projectileKind = store.kind[life] ?? EntityKind.Inactive;
  if (
    (projectileKind === EntityKind.Comet ||
      projectileKind === EntityKind.Asteroid) &&
    store.kind[STAR_INDEX] === EntityKind.Star &&
    Math.hypot(store.x[life] ?? 0, store.y[life] ?? 0) <
      STAR_COLLISION_RADIUS + collisionRadiusForKind(projectileKind)
  ) {
    store.matter?.discard(life, STAR_INDEX);
    store.kind[life] = EntityKind.Inactive;
    return;
  }
  for (const body of CELESTIAL_BODY_SPECS) {
    if (!isSolidBodyKind(store.kind[body.index] ?? EntityKind.Inactive)) {
      continue;
    }
    const combinedRadius =
      body.bodyRadius +
      collisionRadiusForKind(store.kind[life] ?? EntityKind.Inactive);
    const bodyDistance = Math.hypot(
      (store.x[life] ?? 0) - (store.x[body.index] ?? 0),
      (store.y[life] ?? 0) - (store.y[body.index] ?? 0),
    );
    if (bodyDistance < combinedRadius) {
      if (
        impactEmissions !== undefined &&
        (projectileKind === EntityKind.Comet ||
          projectileKind === EntityKind.Asteroid)
      ) {
        let normalX = (store.x[life] ?? 0) - (store.x[body.index] ?? 0);
        let normalY = (store.y[life] ?? 0) - (store.y[body.index] ?? 0);
        [normalX, normalY] = normalizedContactNormal(
          normalX,
          normalY,
          store.lineage[life] ?? life,
          body.index,
        );
        impactEmissions.push(
          projectileKind === EntityKind.Asteroid
            ? EmissionKind.AsteroidImpact
            : EmissionKind.CometImpact,
          (store.x[body.index] ?? 0) + normalX * body.bodyRadius,
          (store.y[body.index] ?? 0) + normalY * body.bodyRadius,
          Math.max(0.8, store.mass[life] ?? 0.8),
          body.index, normalX * body.bodyRadius, normalY * body.bodyRadius,
        );
      }
      store.matter?.discard(life, body.index);
      store.kind[life] = EntityKind.Inactive;
      return;
    }
  }
}

function isSolidBodyKind(kind: EntityKind): boolean {
  return kind === EntityKind.Planet || kind === EntityKind.Moon;
}

function projectOutsideBody(
  store: PrimaryStore,
  life: number,
  body: number,
  combinedRadius: number,
): void {
  let dx = (store.x[life] ?? 0) - (store.x[body] ?? 0);
  let dy = (store.y[life] ?? 0) - (store.y[body] ?? 0);
  const distance = Math.hypot(dx, dy);
  if (distance >= combinedRadius) {
    return;
  }
  [dx, dy] = normalizedContactNormal(
    dx,
    dy,
    store.lineage[life] ?? life,
    body,
  );
  store.x[life] =
    (store.x[body] ?? 0) + dx * (combinedRadius + SEPARATION_EPSILON);
  store.y[life] =
    (store.y[body] ?? 0) + dy * (combinedRadius + SEPARATION_EPSILON);
}

function firstCircleHit(
  startX: number,
  startY: number,
  travelX: number,
  travelY: number,
  radius: number,
): number | undefined {
  const c = startX * startX + startY * startY - radius * radius;
  if (c <= 0) {
    return 0;
  }
  const a = travelX * travelX + travelY * travelY;
  if (a <= 1e-16) {
    return undefined;
  }
  const b = 2 * (startX * travelX + startY * travelY);
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) {
    return undefined;
  }
  const time = (-b - Math.sqrt(discriminant)) / (2 * a);
  return time >= 0 && time <= 1 ? time : undefined;
}

function normalizedContactNormal(
  x: number,
  y: number,
  firstSeed: number,
  secondSeed: number,
): [number, number] {
  const length = Math.hypot(x, y);
  if (length > 1e-10) {
    return [x / length, y / length];
  }
  const seed =
    (Math.imul(firstSeed + 1, 0x9e37_79b1) ^
      Math.imul(secondSeed + 1, 0x85eb_ca77)) >>>
    0;
  const angle = (seed / 0x1_0000_0000) * Math.PI * 2;
  return [Math.cos(angle), Math.sin(angle)];
}

function isSolidBodyCollider(kind: EntityKind): boolean {
  return (
    isLivingKind(kind) ||
    kind === EntityKind.Corpse ||
    kind === EntityKind.Comet ||
    kind === EntityKind.Asteroid
  );
}

function isPairCollider(kind: EntityKind): boolean {
  return (
    kind === EntityKind.Herbivore ||
    kind === EntityKind.Predator ||
    kind === EntityKind.Scavenger ||
    kind === EntityKind.Corpse ||
    kind === EntityKind.Comet ||
    kind === EntityKind.Asteroid
  );
}
